import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeRequest } from '../services/api';
import { cn } from '../lib/utils';
import { formatScore } from '../utils/formatScore';
import { RootState, useAppDispatch } from '../store';
import {
  setSelectedChart,
  clearSelectedChart,
  setChartTicker,
} from '../store/slices/chartSlice';
import { addFavoriteThunk, removeFavoriteThunk } from '../store/thunks';
import { selectFavoritesMap, selectFavoritesLoading } from '../store/selectors';
import { setSelectedTicker } from '../store/slices/analysisPanelSlice';
import {
  requestNavigateMainTab,
  requestDeepSubTab,
  setDeepAnalysisReturnTab,
} from '../store/slices/modalsSlice';
import {
  SEARCH_DEBOUNCE_MS,
  STALE_DATA_THRESHOLD_MS,
} from '../config/featureFlags';
import ChartsGridLightweightCell from './charts/ChartsGridLightweightCell';
import EntryStatusBadge from './common/EntryStatusBadge';
import { getPatternTagLabel } from '../utils/patternTagLabel';

const TOUCH_TARGET_SM = { minHeight: '36px', minWidth: '36px' };
const TOUCH_TARGET_SEARCH = { minHeight: '36px', minWidth: '80px' };

export interface ChartCardItem {
  ticker: string;
  company_name: string;
  sector: string;
  current_price: number;
  day_change_percent: number;
  volume_ratio_20d: number;
  pattern_tags: string[];
  trend_signal: 'bullish' | 'bearish' | 'neutral' | 'bullish_overextended';
  score: number;
  /** Graded entry-status code (populated separately by the backend Charts-tab
   *  payload - Slice B). Optional/undefined-safe: the badge simply hides when
   *  absent. Stable snake_case from `screening_results.entry_status`. */
  entry_status?: string;
  /** Plain-language buy instruction (SPEC_ENTRY_CONVICTION_2026-06-19) - used as
   *  the badge tooltip when present; optional. */
  entry_instruction?: string;
  /** Coarse LIVE membership (PLAN_ENTRY_STATUS_PARITY_2026-06-22) - drives the
   *  badge's live-divergence hint so grid cards match the table for the same
   *  ticker. Optional; absent on an immaterial live gap. */
  live_entry_status?: string;
}

export interface ChartsGridTabProps {
  onChartClick: (ticker: string) => void;
  /** When false, the Charts tab is hidden (keeps mount); skip expensive layout work */
  isActive?: boolean;
}

interface DetectedChartsMeta {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  facet_patterns?: string[];
  facet_sectors?: string[];
}

interface DetectedChartsResponse {
  data: ChartCardItem[];
  meta: DetectedChartsMeta;
}

/** Fixed page size: only this many chart cards load/render per page (no infinite append). */
const CHARTS_PAGE_SIZE = 12;
/** Target minimum column width; combined with MAX_GRID_COLUMNS → up to 4 wide columns (4×3 for 12). */
const MIN_CARD_WIDTH = 268;
/** Never exceed 4 columns so 12 items stay 4×3 (wider cards) instead of 8+4 on ultrawide. */
const MAX_GRID_COLUMNS = 4;
const GRID_GAP = 8;

type SortField =
  | 'ticker'
  | 'day_change_percent'
  | 'volume_ratio_20d'
  | 'current_price'
  | 'score';
type SortDirection = 'asc' | 'desc';

function buildDetectedChartsUrl(params: {
  offset: number;
  limit: number;
  sort: SortField;
  direction: SortDirection;
  search: string;
  patterns: string[];
  sectors: string[];
}): string {
  const sp = new URLSearchParams();
  sp.set('limit', String(params.limit));
  sp.set('offset', String(params.offset));
  sp.set('sort', params.sort);
  sp.set('direction', params.direction);
  const q = params.search.trim().toLowerCase();
  if (q) sp.set('search', q);
  if (params.patterns.length) {
    sp.set('pattern', params.patterns.map((p) => p.toLowerCase()).join(','));
  }
  if (params.sectors.length) {
    sp.set('sector', params.sectors.map((s) => s.toLowerCase()).join(','));
  }
  return `/api/charts/detected?${sp.toString()}`;
}

function getTrendColor(signal: ChartCardItem['trend_signal']) {
  switch (signal) {
    case 'bullish':
      return 'text-success';
    case 'bearish':
      return 'text-danger';
    default:
      return 'text-secondary';
  }
}

const ChartsGridTab: React.FC<ChartsGridTabProps> = ({
  onChartClick,
  isActive = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const screeningLastUpdated = useSelector(
    (state: RootState) => state.screening.last_updated
  );
  const screeningStatus = useSelector(
    (state: RootState) => state.screening.status
  );
  const screeningMessage = useSelector(
    (state: RootState) => state.screening.message
  );
  const screeningProcessed = useSelector(
    (state: RootState) => state.screening.processed_count
  );
  const screeningTotalStocks = useSelector(
    (state: RootState) => state.screening.total_stocks
  );

  // Selection state from Redux
  const selectedTicker = useSelector(
    (state: RootState) => state.chart.selectedTicker
  );
  const selectedPageFromRedux = useSelector(
    (state: RootState) => state.chart.selectedPage
  );

  // Favorites
  const favoritesMap = useSelector(selectFavoritesMap);
  const favoritesLoading = useSelector(selectFavoritesLoading);

  const isScreeningBusy =
    screeningStatus === 'running' || screeningStatus === 'loading';
  /** Monotonic id so only the latest in-flight `loadCharts` call may commit results (overlapping requests safe). */
  const loadChartsRequestIdRef = useRef(0);

  const [charts, setCharts] = useState<ChartCardItem[]>([]);
  const chartsRef = useRef<ChartCardItem[]>([]);
  useEffect(() => {
    chartsRef.current = charts;
  }, [charts]);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);
  /** In-flight page change when grid already has rows (avoid full skeleton flash). */
  const [loadingPage, setLoadingPage] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [facetPatterns, setFacetPatterns] = useState<string[]>([]);
  const [facetSectors, setFacetSectors] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [chartsFiltersOpen, setChartsFiltersOpen] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const [gridContainerWidth, setGridContainerWidth] = useState(1200);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const isStale = useMemo(() => {
    if (!lastFetchTime) return false;
    return Date.now() - lastFetchTime > STALE_DATA_THRESHOLD_MS;
  }, [lastFetchTime]);

  const timeSinceLastFetch = useMemo(() => {
    if (!lastFetchTime) return null;
    const seconds = Math.floor((Date.now() - lastFetchTime) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }, [lastFetchTime]);

  const loadCharts = useCallback(
    async (
      targetPage: number,
      opts?: { skipCache?: boolean; scrollToTop?: boolean }
    ) => {
      if (isScreeningBusy) return;

      void screeningLastUpdated;

      const requestId = ++loadChartsRequestIdRef.current;
      setError(null);

      const startFresh = targetPage === 1;
      const scrollToTop = opts?.scrollToTop ?? false;
      const scrollBefore = scrollAreaRef.current?.scrollTop ?? 0;

      const hasExistingCharts = chartsRef.current.length > 0;
      // Only clear charts if we have existing content AND we're doing a fresh load
      // This prevents scroll collapse when scrolling through pages
      if (startFresh && hasExistingCharts) {
        // Save scroll position before clearing - will restore after load
        void scrollBefore;
      }

      if (startFresh) {
        if (hasExistingCharts) {
          // We have content - just show loading state without clearing
          setLoadingInitial(true);
          setLoadingPage(false);
        } else {
          // First load - clear and show skeleton
          setCharts([]);
          setLoadingInitial(true);
          setLoadingPage(false);
        }
      } else {
        setLoadingPage(true);
      }

      try {
        const offset = (targetPage - 1) * CHARTS_PAGE_SIZE;
        const url = buildDetectedChartsUrl({
          offset,
          limit: CHARTS_PAGE_SIZE,
          sort: sortField,
          direction: sortDirection,
          search: debouncedSearchQuery,
          patterns: selectedPatterns,
          sectors: selectedSectors,
        });

        const response = await makeRequest<DetectedChartsResponse>(
          'GET',
          url,
          undefined,
          {
            cacheTTL: 60000,
            skipCache: opts?.skipCache ?? false,
          }
        );

        if (requestId !== loadChartsRequestIdRef.current) return;

        const rows = response.data || [];
        const meta = response.meta;

        // Restore scroll position after charts update
        // Use requestAnimationFrame to ensure DOM has updated first
        const scrollEl = scrollAreaRef.current;
        const restoredScrollTop = scrollBefore;

        setCharts(rows);
        setPage(targetPage);
        setTotal(meta?.total ?? 0);
        if (meta?.facet_patterns && meta.facet_patterns.length > 0) {
          setFacetPatterns(meta.facet_patterns);
        }
        if (meta?.facet_sectors && meta.facet_sectors.length > 0) {
          setFacetSectors(meta.facet_sectors);
        }
        setLastFetchTime(Date.now());

        // Restore scroll after DOM updates - unless pagination asked for a jump-to-top
        // (so users on short laptop viewports always land on the first row when
        // clicking Previous/Next).
        if (scrollEl && scrollToTop) {
          requestAnimationFrame(() => {
            scrollEl.scrollTop = 0;
          });
        } else if (scrollEl && restoredScrollTop > 0) {
          requestAnimationFrame(() => {
            if (scrollEl.scrollHeight <= scrollEl.clientHeight) {
              // Content fits in viewport - scroll to top
              scrollEl.scrollTop = 0;
            } else {
              // Restore previous scroll position
              scrollEl.scrollTop = restoredScrollTop;
            }
          });
        }
      } catch (err: any) {
        if (requestId !== loadChartsRequestIdRef.current) return;
        let errorMessage = err.message || 'Failed to load charts';
        if (err.type === 'NetworkError') {
          errorMessage =
            'Cannot connect to server. Please check your internet connection.';
        } else if (err.type === 'TimeoutError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (err.status === 404) {
          errorMessage =
            'Charts data not found. The feature may not be available.';
        } else if (err.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        setError(errorMessage);
        setCharts([]);
        setTotal(0);
      } finally {
        if (requestId === loadChartsRequestIdRef.current) {
          setLoadingInitial(false);
          setLoadingPage(false);
        }
      }
    },
    [
      debouncedSearchQuery,
      selectedPatterns,
      selectedSectors,
      sortField,
      sortDirection,
      screeningLastUpdated,
      isScreeningBusy,
    ]
  );

  /** Match results table: clear chart grid as soon as screening runs (including “already running” sync). */
  useEffect(() => {
    if (!isScreeningBusy) return;
    loadChartsRequestIdRef.current += 1;
    setLoadingPage(false);
    setCharts([]);
    setTotal(0);
    setFacetPatterns([]);
    setFacetSectors([]);
    setError(null);
    setLastFetchTime(null);
    setPage(1);
    setLoadingInitial(true);
  }, [isScreeningBusy]);

  /** Consume saved page from Redux once when returning from the Selected Chart view in Deep Analysis, then clear it. */
  const pendingRestorePageRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedTicker && selectedPageFromRedux) {
      pendingRestorePageRef.current = selectedPageFromRedux;
    }
  }, [selectedTicker, selectedPageFromRedux]);

  /** Load page 1 when filters/sort change or screening finishes; restore saved page if returning from the Selected Chart view in Deep Analysis. */
  useEffect(() => {
    if (isScreeningBusy) return;
    const restorePage = pendingRestorePageRef.current;
    if (restorePage) {
      pendingRestorePageRef.current = null;
      dispatch(clearSelectedChart());
      void loadCharts(restorePage);
    } else {
      void loadCharts(1);
    }
  }, [
    debouncedSearchQuery,
    selectedPatterns,
    selectedSectors,
    sortField,
    sortDirection,
    isScreeningBusy,
    screeningLastUpdated,
    loadCharts,
    dispatch,
  ]);

  useEffect(() => {
    if (isStale && !loadingInitial && !loadingPage) {
      const warningTimer = setTimeout(() => {
        console.warn('Charts data may be stale. Consider refreshing.');
      }, 1000);
      return () => clearTimeout(warningTimer);
    }
    return undefined;
  }, [isStale, loadingInitial, loadingPage]);

  const allPatterns = useMemo(() => facetPatterns, [facetPatterns]);
  const allSectors = useMemo(() => facetSectors, [facetSectors]);

  /**
   * Measure the scroll pane whenever the tab is active. Double rAF on mount helps after `hidden` →
   * visible so `clientWidth` reflects the full main column (fixes grid stuck ~half width).
   */
  useLayoutEffect(() => {
    if (!isActive) return undefined;
    const node = scrollAreaRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const w = Math.max(320, Math.floor(rect.width));
      setGridContainerWidth(w);
    };

    updateSize();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(updateSize);
    });

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(node);
    window.addEventListener('resize', updateSize);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [isActive, charts.length]);

  const togglePattern = (pattern: string) => {
    setSelectedPatterns((prev) =>
      prev.includes(pattern)
        ? prev.filter((p) => p !== pattern)
        : [...prev, pattern]
    );
  };

  const toggleSector = (sector: string) => {
    setSelectedSectors((prev) =>
      prev.includes(sector)
        ? prev.filter((s) => s !== sector)
        : [...prev, sector]
    );
  };

  const gridColumns = useMemo(() => {
    // Single column on mobile for list-like display matching the stocks table
    if (gridContainerWidth < 768) return 1;
    const available = Math.max(320, gridContainerWidth);
    const fromWidth = Math.max(
      1,
      Math.floor(available / (MIN_CARD_WIDTH + GRID_GAP))
    );
    return Math.min(MAX_GRID_COLUMNS, fromWidth);
  }, [gridContainerWidth]);

  const totalPages = useMemo(() => {
    if (total <= 0) return 0;
    return Math.ceil(total / CHARTS_PAGE_SIZE);
  }, [total]);

  const pageRangeLabel = useMemo(() => {
    if (total <= 0) return '0 / 0';
    const start = (page - 1) * CHARTS_PAGE_SIZE + 1;
    const end = Math.min(page * CHARTS_PAGE_SIZE, total);
    return `${start}–${end} of ${total}`;
  }, [page, total]);

  const canGoPrev =
    page > 1 && !loadingInitial && !loadingPage && !isScreeningBusy;
  /** Pagination only (no scroll / has_more append - next page is explicit). */
  const canGoNext =
    !loadingInitial && !loadingPage && !isScreeningBusy && page < totalPages;

  const refresh = useCallback(() => {
    void loadCharts(page, { skipCache: true });
  }, [loadCharts, page]);

  const renderError = () => (
    <div className="tv-card">
      <div className="tv-card-body tv-text-center tv-py-8">
        <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
        <h5 className="text-lg font-semibold mb-2">{t('common.error')}</h5>
        <p className="text-secondary mb-4">{error}</p>
        <button className="tv-btn tv-btn-primary" onClick={refresh}>
          <i className="fas fa-redo me-2"></i>
          {t('common.retry')}
        </button>
      </div>
    </div>
  );

  const renderEmpty = () => (
    <div className="flex min-h-[400px] flex-1 items-center justify-center px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto w-full max-w-xl rounded-2xl bg-surface-container-low/70 p-6 text-center shadow-sm md:p-10">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-surface-container">
          <i className="fas fa-chart-bar text-2xl text-text-secondary"></i>
        </div>
        <h5 className="mb-2 text-xl font-semibold text-text-primary md:text-2xl">
          {t('chart.gridEmptyTitle')}
        </h5>
        <p className="mx-auto mb-6 max-w-md text-sm text-text-secondary md:text-base">
          {debouncedSearchQuery ||
          selectedPatterns.length > 0 ||
          selectedSectors.length > 0
            ? t('chart.gridEmptyFiltered')
            : t('chart.gridEmptyDefault')}
        </p>
        {(debouncedSearchQuery ||
          selectedPatterns.length > 0 ||
          selectedSectors.length > 0) && (
          <button
            className="inline-flex min-h-[38px] items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-surface-container-high"
            onClick={() => {
              setSearchQuery('');
              setSelectedPatterns([]);
              setSelectedSectors([]);
            }}
          >
            {t('chart.gridClearFilters')}
          </button>
        )}
      </div>
    </div>
  );

  const toggleFavorite = useCallback(
    (ticker: string, companyName: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const normalized = ticker.trim().toUpperCase();
      if (favoritesMap.has(normalized)) {
        dispatch(removeFavoriteThunk(normalized));
      } else {
        dispatch(
          addFavoriteThunk({
            ticker: normalized,
            color: 'yellow',
            notes: companyName,
          })
        );
      }
    },
    [dispatch, favoritesMap]
  );

  const renderCard = useCallback(
    (chart: ChartCardItem) => {
      const openDetail = () => {
        // Store selection in Redux before navigating
        dispatch(setSelectedChart({ ticker: chart.ticker, page }));
        onChartClick(chart.ticker);
      };
      const isSelected = selectedTicker === chart.ticker;
      const normalizedTicker = chart.ticker.trim().toUpperCase();
      const isFavorited = favoritesMap.has(normalizedTicker);
      return (
        <div
          className={cn(
            /* No outer margin: grid already uses gap; margins overflow fixed h-[420px] cells and clip charts */
            'tv-card h-full min-h-0 flex flex-col overflow-hidden',
            'border hover:tv-shadow-lg transition-shadow cursor-pointer',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary',
            // Highlight selected card
            isSelected
              ? 'border-accent-primary ring-2 ring-accent-primary ring-offset-2 ring-offset-bg-primary'
              : 'border-border-subtle hover:border-accent-primary'
          )}
          role="button"
          tabIndex={0}
          onClick={openDetail}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDetail();
            }
          }}
          aria-label={`Open full chart for ${chart.ticker}`}
          aria-pressed={isSelected}
        >
          <div className="shrink-0 px-2 py-1.5 border-b border-border-subtle">
            {/* Row 1: Ticker, trend, company name | favorite star + price */}
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0 flex-1 flex items-center gap-1.5 pointer-events-none">
                <h6 className="font-bold text-text-primary mb-0 text-xs whitespace-nowrap">
                  {chart.ticker}
                </h6>
                <span
                  className={cn(
                    'text-[11px] font-medium whitespace-nowrap',
                    getTrendColor(chart.trend_signal)
                  )}
                >
                  {chart.trend_signal.toUpperCase()}
                </span>
                <EntryStatusBadge
                  code={chart.entry_status}
                  liveCode={chart.live_entry_status}
                  instruction={chart.entry_instruction}
                  compact
                />
                <span
                  className="text-[11px] text-secondary truncate"
                  title={chart.company_name}
                >
                  {chart.company_name}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="p-2 rounded-md transition-colors pointer-events-auto text-gray-500 hover:text-accent-primary hover:bg-accent-primary/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setSelectedTicker(chart.ticker));
                    dispatch(setChartTicker(chart.ticker));
                    dispatch(setDeepAnalysisReturnTab('charts'));
                    dispatch(requestDeepSubTab('analysis'));
                    dispatch(requestNavigateMainTab('deep_analysis'));
                  }}
                  aria-label={`Deep Analysis for ${chart.ticker}`}
                  title="Deep Analysis"
                >
                  <i className="fas fa-microscope text-sm"></i>
                </button>
                <button
                  type="button"
                  className={cn(
                    'p-2 rounded-md transition-colors pointer-events-auto',
                    isFavorited
                      ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-400/10'
                      : 'text-gray-500 hover:text-yellow-400 hover:bg-yellow-400/10'
                  )}
                  onClick={(e) =>
                    toggleFavorite(chart.ticker, chart.company_name, e)
                  }
                  disabled={favoritesLoading}
                  aria-label={
                    isFavorited
                      ? `Remove ${chart.ticker} from favorites`
                      : `Add ${chart.ticker} to favorites`
                  }
                  title={
                    isFavorited ? 'Remove from favorites' : 'Add to favorites'
                  }
                >
                  <i
                    className={cn(
                      'fa-star text-sm',
                      isFavorited ? 'fas' : 'far'
                    )}
                  ></i>
                </button>
                <div className="text-right pointer-events-none">
                  <div className="text-xs font-bold text-text-primary">
                    ${chart.current_price.toFixed(2)}
                  </div>
                  <div
                    className={cn(
                      'text-[10px] font-medium',
                      chart.day_change_percent >= 0
                        ? 'text-success'
                        : 'text-danger'
                    )}
                  >
                    {chart.day_change_percent >= 0 ? '+' : ''}
                    {chart.day_change_percent.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
            {/* Row 2: Extra details - score, volume ratio, sector */}
            <div className="flex items-center gap-2 mt-0.5 pointer-events-none">
              <span className="text-[11px] text-text-primary font-semibold">
                Score {formatScore(chart.score)}
              </span>
              {chart.volume_ratio_20d > 0 && (
                <span
                  className={cn(
                    'text-[11px] font-medium',
                    chart.volume_ratio_20d >= 1.5
                      ? 'text-success'
                      : 'text-secondary'
                  )}
                >
                  Vol {chart.volume_ratio_20d.toFixed(1)}x
                </span>
              )}
              {chart.sector && (
                <span className="text-[11px] text-secondary truncate">
                  {chart.sector}
                </span>
              )}
            </div>
          </div>
          <div className="flex-1 min-h-0 w-full min-w-0 relative pointer-events-none">
            <ChartsGridLightweightCell
              ticker={chart.ticker}
              isGridVisible={isActive}
            />
          </div>
        </div>
      );
    },
    [
      onChartClick,
      isActive,
      page,
      selectedTicker,
      dispatch,
      favoritesMap,
      favoritesLoading,
      toggleFavorite,
    ]
  );

  const showSkeleton =
    charts.length === 0 && (isScreeningBusy || loadingInitial);

  return (
    <div className="charts-grid-tab flex h-full w-full max-w-none min-h-0 min-w-0 flex-1 flex-col self-stretch overflow-hidden">
      <div className="tv-card mb-0 w-full max-w-none shrink-0 self-stretch md:mb-1">
        <div className="tv-card-header tv-py-0.5 md:tv-py-0.5 md:tv-px-4 tv-px-2 bg-surface-container-high border-b border-border-subtle">
          <div className="tv-flex tv-justify-between tv-items-center">
            <div className="tv-flex tv-items-center tv-gap-2 tv-min-w-0">
              <h5 className="text-sm font-bold m-0 text-text-primary truncate">
                <i className="fas fa-chart-bar me-1 md:me-2"></i>
                <span className="hidden sm:inline">
                  Detected Chart Patterns
                </span>
                <span className="sm:hidden">Charts</span>
                <span className="hidden md:inline"> ({total})</span>
              </h5>
              {isStale && !loadingInitial && !isScreeningBusy && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning tv-flex tv-items-center tv-gap-1"
                  title={`Data last updated ${timeSinceLastFetch}. Values may be outdated.`}
                >
                  <i className="fas fa-exclamation-triangle"></i>
                  <span className="hidden md:inline">Stale</span>
                </span>
              )}
            </div>
            <div className="tv-flex tv-items-center tv-gap-2">
              {timeSinceLastFetch && !loadingInitial && !isScreeningBusy && (
                <small className="text-secondary text-xs hidden md:inline">
                  Updated {timeSinceLastFetch}
                </small>
              )}
              <button
                className="tv-btn tv-btn-sm tv-btn-outline inline-flex items-center justify-center min-h-[36px] min-w-[36px]"
                onClick={refresh}
                disabled={
                  isScreeningBusy || (loadingInitial && charts.length === 0)
                }
                style={{
                  borderColor: 'var(--tv-border-primary)',
                  color: 'var(--tv-text-secondary)',
                }}
              >
                <i
                  className={cn(
                    'fas fa-sync-alt me-1',
                    loadingInitial && !isScreeningBusy && 'fa-spin'
                  )}
                ></i>
                <span className="hidden md:inline">{t('common.refresh')}</span>
              </button>
            </div>
          </div>
        </div>

        <div
          className="tv-card-body tv-border-bottom tv-py-0.5 tv-px-2 sm:tv-py-0.5 sm:tv-px-4"
          style={{ borderBottomColor: 'var(--tv-border-primary)' }}
        >
          {/* Mobile: compact single row - search + sort + filter toggle */}
          <div className="flex md:hidden items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                className="tv-form-control tv-pl-10 w-full"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  backgroundColor: 'var(--tv-bg-tertiary)',
                  borderColor: 'var(--tv-border-primary)',
                  color: 'var(--tv-text-primary)',
                  paddingLeft: '2.5rem',
                  ...TOUCH_TARGET_SEARCH,
                }}
              />
              <i
                className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
                style={{ zIndex: 1 }}
              ></i>
            </div>
            <select
              className="tv-form-select tv-form-select-sm shrink-0"
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value as SortField);
                setSortDirection('desc');
              }}
              aria-label="Sort charts"
              style={{
                backgroundColor: 'var(--tv-bg-tertiary)',
                borderColor: 'var(--tv-border-primary)',
                color: 'var(--tv-text-primary)',
                fontSize: '0.7rem',
                minHeight: '36px',
                width: '80px',
              }}
            >
              <option value="score">Score</option>
              <option value="day_change_percent">Day %</option>
              <option value="ticker">Ticker</option>
              <option value="volume_ratio_20d">Vol</option>
              <option value="current_price">Price</option>
            </select>
            <button
              className="btn btn-sm btn-outline-secondary shrink-0"
              onClick={() =>
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
              }
              aria-label="Toggle sort direction"
              style={{ fontSize: '0.75rem', ...TOUCH_TARGET_SM }}
            >
              <i
                className={cn(
                  'fas',
                  sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                )}
              ></i>
            </button>
            {(allPatterns.length > 0 || allSectors.length > 0) && (
              <button
                className={cn(
                  'btn btn-sm shrink-0',
                  chartsFiltersOpen ? 'btn-primary' : 'btn-outline-secondary'
                )}
                onClick={() => setChartsFiltersOpen((v) => !v)}
                aria-label="Toggle filters"
                aria-expanded={chartsFiltersOpen}
                style={{ ...TOUCH_TARGET_SM }}
              >
                <i className="fas fa-filter"></i>
                {selectedPatterns.length + selectedSectors.length > 0 && (
                  <span className="ml-1 text-[10px]">
                    {selectedPatterns.length + selectedSectors.length}
                  </span>
                )}
              </button>
            )}
          </div>
          {/* Mobile: collapsible pattern/sector filters */}
          {chartsFiltersOpen && (
            <div className="md:hidden mt-2 flex flex-col gap-1.5">
              {allPatterns.length > 0 && (
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <small className="shrink-0 text-xs text-secondary">
                    Patterns:
                  </small>
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1 pb-0.5">
                    {allPatterns.slice(0, 8).map((pattern) => (
                      <button
                        key={pattern}
                        type="button"
                        className={cn(
                          'btn btn-sm flex min-h-8 min-w-10 shrink-0',
                          selectedPatterns.includes(pattern)
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                        )}
                        style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.45rem',
                        }}
                        onClick={() => togglePattern(pattern)}
                        title={pattern}
                      >
                        {getPatternTagLabel(pattern)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {allSectors.length > 0 && (
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <small className="shrink-0 text-xs text-secondary">
                    Sectors:
                  </small>
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1 pb-0.5">
                    {allSectors.slice(0, 6).map((sector) => (
                      <button
                        key={sector}
                        type="button"
                        className={cn(
                          'btn btn-sm flex min-h-8 min-w-10 shrink-0',
                          selectedSectors.includes(sector)
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                        )}
                        style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.45rem',
                        }}
                        onClick={() => toggleSector(sector)}
                      >
                        {sector}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Mobile: page info + navigation - single tight row */}
          <div className="md:hidden mt-1 flex items-center justify-center gap-2">
            <button
              type="button"
              className="px-2 py-1 rounded bg-surface-container-highest text-text-primary text-[11px] font-medium disabled:opacity-30"
              style={{ minWidth: 28, minHeight: 28 }}
              disabled={!canGoPrev}
              onClick={() => void loadCharts(page - 1, { scrollToTop: true })}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left" aria-hidden />
            </button>
            <span className="text-[11px] text-secondary leading-none">
              {pageRangeLabel}
            </span>
            {totalPages > 0 && (
              <span className="text-[10px] text-secondary leading-none opacity-90">
                P{page}/{totalPages}
              </span>
            )}
            <button
              type="button"
              className="px-2 py-1 rounded bg-surface-container-highest text-text-primary text-[11px] font-medium disabled:opacity-30"
              style={{ minWidth: 28, minHeight: 28 }}
              disabled={!canGoNext}
              onClick={() => void loadCharts(page + 1, { scrollToTop: true })}
              aria-label="Next page"
            >
              <i className="fas fa-chevron-right" aria-hidden />
            </button>
          </div>

          {/* Desktop: compact filter layout (optimized for 3 rows max) */}
          <div className="hidden md:flex flex-col md:flex-row md:items-start md:gap-2 lg:gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="relative">
                <input
                  type="text"
                  className="tv-form-control tv-pl-10"
                  placeholder={t('common.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    backgroundColor: 'var(--tv-bg-tertiary)',
                    borderColor: 'var(--tv-border-primary)',
                    color: 'var(--tv-text-primary)',
                    paddingLeft: '2.5rem',
                    minHeight: '30px',
                    minWidth: '80px',
                  }}
                />
                <i
                  className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
                  style={{ zIndex: 1 }}
                ></i>
              </div>

              {/* Compact combined pattern/sector row */}
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                <small className="shrink-0 text-xs text-secondary">
                  Filters:
                </small>
                <div className="flex min-w-0 flex-1 flex-wrap gap-0.5 overflow-x-auto">
                  {allPatterns.slice(0, 8).map((pattern) => (
                    <button
                      key={pattern}
                      type="button"
                      className={cn(
                        'btn btn-sm flex min-h-6 min-w-8 shrink-0',
                        selectedPatterns.includes(pattern)
                          ? 'btn-primary'
                          : 'btn-outline-secondary'
                      )}
                      style={{
                        fontSize: '0.6rem',
                        padding: '0.05rem 0.3rem',
                      }}
                      onClick={() => togglePattern(pattern)}
                      title={pattern}
                    >
                      {getPatternTagLabel(pattern)}
                    </button>
                  ))}
                  {allSectors.slice(0, 6).map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      className={cn(
                        'btn btn-sm flex min-h-6 min-w-8 shrink-0',
                        selectedSectors.includes(sector)
                          ? 'btn-primary'
                          : 'btn-outline-secondary'
                      )}
                      style={{
                        fontSize: '0.6rem',
                        padding: '0.05rem 0.3rem',
                      }}
                      onClick={() => toggleSector(sector)}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1 sm:pl-2">
              <div className="flex items-center gap-1 sm:gap-2">
                <small className="text-secondary text-xs whitespace-nowrap">
                  Sort:
                </small>
                <select
                  className="tv-form-select tv-form-select-sm"
                  value={sortField}
                  onChange={(e) => {
                    setSortField(e.target.value as SortField);
                    setSortDirection('desc');
                  }}
                  aria-label="Sort charts"
                  title="Sort charts"
                  style={{
                    backgroundColor: 'var(--tv-bg-tertiary)',
                    borderColor: 'var(--tv-border-primary)',
                    color: 'var(--tv-text-primary)',
                    fontSize: '0.75rem',
                    minHeight: '36px',
                    minWidth: '90px',
                  }}
                >
                  <option value="score">Score</option>
                  <option value="ticker">Ticker</option>
                  <option value="day_change_percent">Day %</option>
                  <option value="volume_ratio_20d">Volume Ratio</option>
                  <option value="current_price">Price</option>
                </select>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    setSortDirection((prev) =>
                      prev === 'asc' ? 'desc' : 'asc'
                    )
                  }
                  aria-label="Toggle sort direction"
                  title="Toggle sort direction"
                  style={{ fontSize: '0.75rem', ...TOUCH_TARGET_SM }}
                >
                  <i
                    className={cn(
                      'fas',
                      sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                    )}
                  ></i>
                </button>
              </div>

              {/* Compact single-line pagination */}
              <div className="flex items-center justify-center gap-1 sm:items-end">
                <span className="text-xs text-secondary whitespace-nowrap">
                  {pageRangeLabel}
                  {totalPages > 0 && (
                    <span className="ml-1">
                      · Page {page}/{totalPages}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isScreeningBusy && (
        <div
          className="max-md:mb-1 mb-2 shrink-0 rounded-lg bg-surface-container-high/90 px-2 py-1.5 text-xs md:px-3 md:py-2 md:text-sm text-text-primary"
          role="status"
          aria-live="polite"
        >
          <i
            className="fas fa-spinner fa-spin me-2 text-accent-primary"
            aria-hidden
          />
          <span>{screeningMessage || 'Screening in progress…'}</span>
          {screeningTotalStocks > 0 && (
            <span className="ms-2 text-xs text-secondary">
              {screeningProcessed} / {screeningTotalStocks}
            </span>
          )}
        </div>
      )}

      {showSkeleton ? (
        <div
          ref={scrollAreaRef}
          className="charts-grid-scroll flex w-full max-w-none min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
        >
          <div className="tv-card flex w-full max-w-none min-h-0 min-w-0 flex-1 flex-col self-stretch">
            <div className="tv-card-body w-full max-w-none tv-p-1 md:tv-p-2">
              <div
                className="grid w-full gap-2"
                style={{
                  gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                }}
              >
                {Array.from({ length: CHARTS_PAGE_SIZE }).map((_, i) => (
                  <div
                    key={i}
                    className="min-w-0 tv-card animate-pulse max-md:h-[min(50vh,400px)] max-md:min-h-[220px] md:h-[320px] md:min-h-[280px]"
                  >
                    <div className="tv-card-body tv-p-4">
                      <div className="h-4 bg-gray-700 rounded w-1/3 mb-3"></div>
                      <div className="h-3 bg-gray-700 rounded w-2/3 mb-4"></div>
                      <div className="h-20 bg-gray-700 rounded mb-3"></div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-700 rounded w-16"></div>
                        <div className="h-6 bg-gray-700 rounded w-16"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : error ? (
        renderError()
      ) : charts.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          <div
            ref={scrollAreaRef}
            className="charts-grid-scroll flex w-full max-w-none min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
          >
            <div className="tv-card flex w-full max-w-none min-h-0 min-w-0 flex-1 flex-col self-stretch">
              <div className="tv-card-body w-full max-w-none tv-p-1 md:tv-p-2">
                <div
                  className="grid w-full gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                  }}
                >
                  {charts.map((c) => (
                    <div
                      key={c.ticker}
                      className="flex min-w-0 flex-col overflow-hidden max-md:h-[min(50vh,400px)] max-md:min-h-[220px] md:h-[320px] md:min-h-[280px]"
                    >
                      {renderCard(c)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="tv-shrink-0 border-t border-border-subtle hidden md:block">
            <div className="tv-flex tv-items-center tv-justify-center tv-gap-2 tv-py-1 tv-px-2">
              <button
                type="button"
                className="tv-btn tv-btn-sm tv-btn-outline"
                disabled={!canGoPrev}
                onClick={() => void loadCharts(page - 1, { scrollToTop: true })}
                aria-label="Previous page of charts"
              >
                <i className="fas fa-chevron-left me-1" aria-hidden />
                Previous
              </button>
              <span className="text-xs text-secondary whitespace-nowrap">
                {totalPages > 0 ? `Page ${page} / ${totalPages}` : '-'}
              </span>
              <button
                type="button"
                className="tv-btn tv-btn-sm tv-btn-outline"
                disabled={!canGoNext}
                onClick={() => void loadCharts(page + 1, { scrollToTop: true })}
                aria-label="Next page of charts"
              >
                Next
                <i className="fas fa-chevron-right ms-1" aria-hidden />
              </button>
              {loadingPage && (
                <span
                  className="text-[10px] text-secondary ml-2"
                  role="status"
                  aria-live="polite"
                >
                  Loading…
                </span>
              )}
            </div>
          </div>
          <p className="hidden md:block shrink-0 border-t border-border-subtle py-0.5 text-center text-[10px] leading-tight text-secondary text-text-muted">
            {CHARTS_PAGE_SIZE} symbols per page (4×3 on wide screens). Mini
            charts use ~1 year of daily data from your database (MA 150/200).
            Open a card for the full chart or TradingView.
          </p>
        </>
      )}
    </div>
  );
};

export default ChartsGridTab;
